// Single source of truth for status / priority / entity presentation.
import { Drop, ForkKnife, Bed } from '@phosphor-icons/react';

// key must match a backend status string (subset === the DB CHECK constraint).
// `tw` is the Tailwind color suffix (status-<tw>). `displayOnly: true` marks
// legacy/import-only statuses the backend never sets through a workflow — they
// render with a colour but are excluded from filter/change controls
// (see OPERATIONAL_STATUSES).
export const STATUS = {
  // ── core operational lifecycle ──
  new:             { label: 'New',             tw: 'new' },
  under_process:   { label: 'Under Process',   tw: 'under-process' },
  in_progress:     { label: 'In Progress',     tw: 'under-process' },
  escalated:       { label: 'Escalated',       tw: 'escalated' },
  dept_action_pending:      { label: 'Dept. Action Pending',     tw: 'pending-finance' },
  pending_customer_contact: { label: 'Pending Customer Contact', tw: 'pending-finance' },
  pending_finance: { label: 'Pending Finance', tw: 'pending-finance' },
  reopened:        { label: 'Reopened',        tw: 'escalated' },
  resolved:        { label: 'Resolved',        tw: 'resolved' },
  closed:          { label: 'Closed',          tw: 'closed', neutral: true },

  // ── display-only (legacy / import artifacts — backend never sets via workflow) ──
  open:                { label: 'Open',              tw: 'open',            neutral: true, displayOnly: true },
  acknowledged:        { label: 'Acknowledged',      tw: 'open',            neutral: true, displayOnly: true },
  dept_confirmed:      { label: 'Dept. Confirmed',   tw: 'resolved',                       displayOnly: true },
  pending_third_party: { label: 'Pending 3rd Party', tw: 'pending-finance',                displayOnly: true },
};

// Statuses safe to offer in filter/change controls (excludes displayOnly).
export const OPERATIONAL_STATUSES = Object.entries(STATUS)
  .filter(([, cfg]) => !cfg.displayOnly)
  .map(([key]) => key);

// Fallback for any status string not in STATUS (should never happen for
// portal-originated data — the DB CHECK constraint is the guarantee — but keeps
// unknown/legacy values rendering as a neutral grey chip instead of a blank cell).
export const UNKNOWN_STATUS_FALLBACK = {
  label: null,
  tw: 'closed',
  classes: 'bg-status-closed/10 text-ink-muted',
  dot: 'bg-status-closed',
};

// Static class maps (kept explicit so Tailwind's purge keeps them).
export const STATUS_CLASSES = {
  new:             'bg-status-new/10 text-status-new',
  under_process:   'bg-status-under-process/10 text-status-under-process',
  in_progress:     'bg-status-under-process/10 text-status-under-process',
  escalated:       'bg-status-escalated/10 text-status-escalated',
  reopened:        'bg-status-escalated/10 text-status-escalated',
  dept_action_pending:      'bg-status-pending-finance/12 text-status-pending-finance',
  pending_customer_contact: 'bg-status-pending-finance/12 text-status-pending-finance',
  pending_finance: 'bg-status-pending-finance/12 text-status-pending-finance',
  pending_third_party:      'bg-status-pending-finance/12 text-status-pending-finance',
  resolved:        'bg-status-resolved/12 text-status-resolved',
  dept_confirmed:  'bg-status-resolved/12 text-status-resolved',
  open:            'bg-status-open/10 text-ink-muted',
  acknowledged:    'bg-status-open/10 text-ink-muted',
  closed:          'bg-status-closed/10 text-ink-muted',
};
export const STATUS_DOT = {
  new: 'bg-status-new',
  under_process: 'bg-status-under-process', in_progress: 'bg-status-under-process',
  escalated: 'bg-status-escalated', reopened: 'bg-status-escalated',
  dept_action_pending: 'bg-status-pending-finance', pending_customer_contact: 'bg-status-pending-finance',
  pending_finance: 'bg-status-pending-finance', pending_third_party: 'bg-status-pending-finance',
  resolved: 'bg-status-resolved', dept_confirmed: 'bg-status-resolved',
  open: 'bg-status-open', acknowledged: 'bg-status-open',
  closed: 'bg-status-closed',
};

export const PRIORITY = {
  low:      { label: 'Low',      dot: 'bg-ink-faint', text: 'text-ink-muted' },
  medium:   { label: 'Medium',   dot: 'bg-ink-faint', text: 'text-ink-muted' },
  high:     { label: 'High',     dot: 'bg-status-pending-finance', text: 'text-status-pending-finance' },
  critical: { label: 'Critical', dot: 'bg-status-escalated', text: 'text-status-escalated' },
};

// keyed by DB entity_slug; tw = short Tailwind colour family (entity-<tw>-*)
export const ENTITY = {
  'abraaj-water':    { label: 'Abraaj',  tw: 'abraaj',  Icon: Drop,      chip: 'bg-entity-abraaj/8 text-entity-abraaj-chip',    spine: 'bg-entity-abraaj-accent' },
  'lofat-lifestyle': { label: 'Lofat',   tw: 'lofat',   Icon: ForkKnife, chip: 'bg-entity-lofat/12 text-entity-lofat-chip',     spine: 'bg-entity-lofat' },
  'regency-kuwait':  { label: 'Regency', tw: 'regency', Icon: Bed,       chip: 'bg-entity-regency/18 text-entity-regency-chip', spine: 'bg-entity-regency' },
};

// Canonical 8-step lifecycle (step 5 Pending Finance only on refund_request / cancel_order).
export const STEPS = ['Open', 'Under Process', 'Assigned', 'Investigating', 'Pending Finance', 'Resolved', 'Customer Confirmed', 'Closed'];
